import type { CustomerPortalRpcClient } from "@redotech/customer-portal-sdk/rpc/client";
import { IterableMap } from "@redotech/react-util/component";
import { useLoad } from "@redotech/react-util/load";
import {
  getItemsViewedAndInCart,
  logPageClick,
} from "@redotech/redo-customer-portal-app/api";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  DiscountCollectionsSelectionType,
  DiscountType,
  ExpirationType,
} from "@redotech/redo-model/discount";
import { Provider } from "@redotech/redo-model/order";
import { ProductFilter } from "@redotech/redo-model/product-filters/product-filters-db-parser";
import { RedoRecommendedProduct } from "@redotech/redo-model/redo-recommended-product-schema";
import {
  upsellCardDefaultText,
  UpsellProductSection,
} from "@redotech/redo-model/team";
import {
  Trackable,
  TrackableFulfillment,
  TrackableType,
} from "@redotech/redo-model/trackable";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { ExternalLink } from "@redotech/redo-web/link";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import {
  getAllCollections,
  getCollection,
  getProductsById,
  getProductsByProductRecommendationFilter,
  getRecommendedBestSellingProducts,
  ProductInfo,
  ShopifyStorefrontClient,
} from "@redotech/shopify-storefront";
import { isDef } from "@redotech/util/checks";
import {
  convertShopifyIdToGid,
  Gid,
  ShopifyIdType,
} from "@redotech/util/shopify-id";
import { assertNever } from "@redotech/util/type";
import * as classNames from "classnames";
import { memo, useContext } from "react";
import {
  DiscountResponse,
  getDiscountFlowResults,
  getOrCreateDiscountCode,
} from "../../../utils/discount-code";
import { redirectToWebsiteWithDiscountCode } from "../../../utils/utils";
import { WindowContext } from "../../window";
import * as trackingCss from "../order-tracking.module.css";
import { validDiscount } from "./discount-util";
import { ExpirationCountdown } from "./expiration-countdown";
import * as fulfillmentCss from "./fulfillment.module.css";
import { RecommendedProductComponent } from "./recommended-product";

export const UpsellProductsCard = memo(function UpsellProductsCard({
  fulfillment,
  settings,
  upsellText,
  onUpsellSectionSelect,
  editMode,
  upsellSectionHighlighted,
  storefrontClient,
  upsellSectionSettings,
  trackable,
  rpcClient,
  getProductFilter,
}: {
  fulfillment: TrackableFulfillment | null;
  settings: ReturnAppSettings;
  upsellText?: string;
  onUpsellSectionSelect?: () => void;
  editMode: boolean;
  upsellSectionHighlighted?: boolean;
  storefrontClient?: ShopifyStorefrontClient | null;
  upsellSectionSettings?: UpsellProductSection;
  trackable: Trackable;
  rpcClient: CustomerPortalRpcClient | undefined;
  getProductFilter?: (
    productFilterId: string,
  ) => Promise<ProductFilter | undefined>;
}) {
  const recommendedProductsLoad = useLoad(
    async (): Promise<RedoRecommendedProduct[]> => {
      if (
        (trackable.provider === Provider.COMMENTSOLD &&
          !settings?.useShopifyStorefrontForCommentSold) ||
        trackable.provider === Provider.BIGCOMMERCE
      ) {
        if (!upsellSectionSettings) return [];
        const recommendedProducts = await fetchRecommendedProductsByProvider(
          trackable.provider,
          10,
          upsellSectionSettings,
          trackable.lineItems
            .map((lineItem) => lineItem.variantId)
            .filter((v) => isDef(v)),
          trackable.lineItems
            .map((lineItem) => lineItem.productId)
            .filter((p) => isDef(p)),
          rpcClient,
        );
        return recommendedProducts
          .filter(
            (product) => product.availableForSale && product.vendor !== "re:do",
          )
          .sort(() => Math.random() - 0.5)
          .slice(0, 3);
      }

      if (!storefrontClient) return [];

      if (!editMode && trackable.provider === Provider.SHOPIFY) {
        const viewedAndAddedToCartProducts = await getItemsViewedAndInCart({
          team: trackable.teamId,
          email: trackable.originalOrder.shopify.contact_email,
          date: trackable.createdAt.toISOString(),
        });

        if (viewedAndAddedToCartProducts.data.length > 0) {
          const fetchedProducts = await fetchProductsByIds(
            viewedAndAddedToCartProducts.data,
            storefrontClient,
            trackable,
          );
          const items = fetchedProducts.map((product) => ({
            ...product,
            altImage: {
              url: product.images.nodes?.[0]?.url,
              altText: product.images.nodes?.[0]?.altText ?? undefined,
            },
            price: product.priceRange.minVariantPrice.amount,
            provider: Provider.SHOPIFY,
          }));
          if (items.length >= 3) return items.slice(0, 3);
        }
      }

      const recommendedProducts = await fetchRecommendedProducts(
        storefrontClient,
        upsellSectionSettings,
        getProductFilter,
        rpcClient,
        trackable,
        3,
      );
      return recommendedProducts.filter(
        (product) => product.availableForSale && product.vendor !== "re:do",
      );
    },
    editMode
      ? [
          upsellSectionSettings?.source,
          upsellSectionSettings?.metafield,
          upsellSectionSettings?.metafieldKey,
          upsellSectionSettings?.collectionName,
        ]
      : [storefrontClient, trackable.lineItems],
  );

  // Fetch discount flow results and discount code
  const discountFlowLoad = useLoad<
    { discountResponse?: DiscountResponse; discountCode?: string } | undefined
  >(async () => {
    if (trackable.provider !== Provider.SHOPIFY) {
      return undefined;
    }

    const orderId =
      trackable.type === TrackableType.RETURN
        ? trackable.originalOrder.id
        : trackable.id;

    const returnTypes =
      trackable.type === TrackableType.RETURN
        ? [trackable.returnType]
        : undefined;

    const discountObject = await getDiscountFlowResults(
      orderId,
      trackable.type,
      returnTypes,
    );

    if (!discountObject?.settings?.enabled) {
      return undefined;
    }

    const discountCode = await getDiscountCode(discountObject, trackable);

    return { discountResponse: discountObject, discountCode };
  }, [trackable.id]);

  const collectionsLoad = useLoad(async () => {
    const discountSettings =
      discountFlowLoad.value?.discountResponse?.settings?.settings;
    // @ts-expect-error: strict-null-checks
    if (discountSettings.discountType !== DiscountType.PRODUCT) {
      return undefined;
    }

    if (!storefrontClient) return [];

    const collections = await getAllCollections(storefrontClient);

    const discountCollections = await Promise.all(
      // @ts-expect-error: strict-null-checks
      discountSettings.discountCollections.map((collection) =>
        getCollection(storefrontClient, collection.id),
      ),
    );

    if (
      // @ts-expect-error: strict-null-checks
      discountSettings.discountCollectionsSelectionType ===
      DiscountCollectionsSelectionType.INCLUDE
    ) {
      return discountCollections;
    } else {
      return collections.filter((collection) =>
        discountCollections.some(
          // @ts-expect-error: strict-null-checks
          (discountCollection) => discountCollection.id !== collection.id,
        ),
      );
    }
  }, [discountFlowLoad.value]);

  const getUpsellText = (): string => {
    let upsellText = upsellCardDefaultText;
    if (
      upsellSectionSettings?.text &&
      upsellSectionSettings?.text !== upsellCardDefaultText
    ) {
      return upsellSectionSettings.text;
    }
    if (
      discountFlowLoad?.value?.discountResponse &&
      validDiscount(
        // @ts-expect-error: strict-null-checks
        discountFlowLoad.value.discountResponse.settings,
        trackable,
      ) &&
      discountFlowLoad.value.discountResponse.shortDescription
    ) {
      upsellText = `Shop again, get ${discountFlowLoad.value.discountResponse.shortDescription}`;
    }
    return upsellText;
  };

  const getInitialExpirationDuration = (): number | undefined => {
    if (discountFlowLoad.value?.discountResponse) {
      if (
        // @ts-expect-error: strict-null-checks
        discountFlowLoad.value?.discountResponse.settings.settings
          .expirationType === ExpirationType.DELIVERY &&
        fulfillment?.trackers[0]?.est_delivery_date
      ) {
        const futureInstant = Temporal.Instant.from(
          fulfillment?.trackers[0]?.est_delivery_date,
        );
        const durationUntilFutureInMs = Temporal.Now.instant()
          .until(futureInstant)
          .total({ unit: "milliseconds" });
        if (durationUntilFutureInMs < 0) {
          return undefined;
        }
        return durationUntilFutureInMs;
      } else {
        const expirationDays =
          // @ts-expect-error: strict-null-checks
          discountFlowLoad.value.discountResponse.settings.settings
            .expirationDays ?? 10;
        const duration = Temporal.Duration.from({ hours: expirationDays * 24 });
        const endDate = Temporal.Instant.from(
          trackable.createdAt.toISOString(),
        ).add(duration);
        const futureInstant = Temporal.Instant.from(endDate);
        const durationUntilFutureInMs = Temporal.Now.instant()
          .until(futureInstant)
          .total({ unit: "milliseconds" });
        return durationUntilFutureInMs > 0
          ? durationUntilFutureInMs
          : undefined;
      }
    }
    return undefined;
  };

  const initialMilliseconds = getInitialExpirationDuration();
  const selectWindow = useContext(WindowContext);

  const storeURL = `https://${
    settings.exchanges.shopSiteDomain || settings.storeUrl
  }`;

  const borderClasses = classNames(
    fulfillmentCss.upsellProductsCardBorder,
    ...(editMode ? [fulfillmentCss.editMode] : []),
    ...(upsellSectionHighlighted ? [fulfillmentCss.highlighted] : []),
  );

  if (recommendedProductsLoad.pending || discountFlowLoad.pending) {
    return (
      <Card className={fulfillmentCss.upsellProductsCard}>
        <Text fontSize="lg" fontWeight="semibold">
          Loading deals...
        </Text>

        <LoadingRedoAnimation />
      </Card>
    );
  } else {
    const upsellRedirect = getUpsellRedirect(
      trackable,
      settings,
      // @ts-expect-error: strict-null-checks
      selectWindow,
      discountFlowLoad.value?.discountCode,
    );

    return (
      <div className={borderClasses}>
        <Card
          className={classNames(fulfillmentCss.upsellProductsCard, {
            // @ts-expect-error: strict-null-checks
            [fulfillmentCss.editMode]: editMode,
          })}
          onClick={onUpsellSectionSelect}
        >
          <Flex dir="column" inert={editMode}>
            <Flex
              align="center"
              className={trackingCss.upsellHeader}
              justify="space-between"
            >
              <Text fontSize="lg" fontWeight="semibold">
                {upsellText || getUpsellText()}
              </Text>
              <ExternalLink
                onClick={async () =>
                  await upsellRedirect.logUpsellAndRedirect()
                }
              >
                Shop full store
              </ExternalLink>
            </Flex>
            {initialMilliseconds && (
              <ExpirationCountdown initialMilliseconds={initialMilliseconds} />
            )}
          </Flex>

          <Flex dir="column" flex={1} inert={editMode} justify="space-between">
            {recommendedProductsLoad.value ? (
              <IterableMap
                items={recommendedProductsLoad.value}
                keyFn={(item: RedoRecommendedProduct) => item.id}
              >
                {(product, index) => (
                  <RecommendedProductComponent
                    collectionsLoad={collectionsLoad}
                    discountResponse={discountFlowLoad.value?.discountResponse}
                    imageAspectRatio={upsellSectionSettings?.imageAspectRatio}
                    index={index}
                    product={product}
                    settings={settings}
                    trackable={trackable}
                    upsellRedirect={getUpsellRedirect(
                      trackable,
                      settings,
                      // @ts-expect-error: strict-null-checks
                      selectWindow,
                      discountFlowLoad.value?.discountCode,
                      `${storeURL}/products/${product.handle}`,
                    )}
                  />
                )}
              </IterableMap>
            ) : null}
          </Flex>
        </Card>
      </div>
    );
  }
});

const fetchRecommendedProducts = async (
  storefrontClient: ShopifyStorefrontClient,
  upsellSectionSettings: UpsellProductSection | undefined,
  getProductFilter:
    | ((productFilterId: string) => Promise<ProductFilter | undefined>)
    | undefined,
  rpcClient: CustomerPortalRpcClient | undefined,
  trackable: Trackable,
  numRecommendations: number,
): Promise<RedoRecommendedProduct[]> => {
  if (!upsellSectionSettings?.recommendedProductFilterId) {
    return getRecommendedBestSellingProducts(
      storefrontClient,
      numRecommendations * 5,
    ).then((products) =>
      products.sort(() => Math.random() - 0.5).slice(0, numRecommendations),
    );
  }

  const recommendedProductsFilter = getProductFilter
    ? await getProductFilter(upsellSectionSettings.recommendedProductFilterId)
    : await rpcClient?.getProductFilterById({
        id: upsellSectionSettings.recommendedProductFilterId,
      });

  if (!recommendedProductsFilter) {
    return getRecommendedBestSellingProducts(
      storefrontClient,
      numRecommendations * 5,
    ).then((products) =>
      products.sort(() => Math.random() - 0.5).slice(0, numRecommendations),
    );
  }

  const excludedProductIds = trackable.lineItems
    .map((lineItem) =>
      lineItem.productId
        ? convertShopifyIdToGid(lineItem.productId, ShopifyIdType.Product)
        : undefined,
    )
    .filter((productId): productId is Gid => productId !== undefined);

  const recommendedProducts = await getProductsByProductRecommendationFilter(
    storefrontClient,
    recommendedProductsFilter,
    excludedProductIds,
  );

  return recommendedProducts
    .filter((product) => product.availableForSale && product.vendor !== "re:do")
    .sort(() => Math.random() - 0.5)
    .slice(0, numRecommendations);
};

const fetchRecommendedProductsByProvider = async (
  provider: Provider,
  numRecommendations: number,
  strategy: UpsellProductSection,
  excludedVariantIds: string[],
  excludedParentProductIds: string[],
  rpcClient: CustomerPortalRpcClient | undefined,
): Promise<RedoRecommendedProduct[]> => {
  if (!rpcClient) return [];
  try {
    const { products } = await rpcClient.getRecommendedProductsByProvider({
      provider,
      numRecommendations,
      strategy,
      excludedVariantIds,
      excludedParentProductIds,
    });
    return products;
  } catch (e) {
    console.error("Error getting cs recommended products", e);

    return [];
  }
};

const fetchProductsByIds = async (
  ids: string[],
  storefrontClient: ShopifyStorefrontClient,
  trackable: Trackable,
): Promise<ProductInfo[]> => {
  const products = await getProductsById(
    storefrontClient,
    ids.map((id) => convertShopifyIdToGid(id, ShopifyIdType.Product)),
  );

  return products.filter((product) => {
    const productGid = convertShopifyIdToGid(
      product.id.toString(),
      ShopifyIdType.Product,
    );
    const productIsNotPresentInTrackable = !trackable.lineItems.some(
      (lineItem) => {
        const lineItemGid = convertShopifyIdToGid(
          // @ts-expect-error: strict-null-checks
          lineItem.productId,

          ShopifyIdType.Product,
        );
        return productGid === lineItemGid;
      },
    );
    return product.availableForSale && productIsNotPresentInTrackable;
  });
};

async function getDiscountCode(
  discountResponse: DiscountResponse,
  trackable: Trackable,
) {
  // @ts-expect-error: strict-null-checks
  if (validDiscount(discountResponse.settings, trackable)) {
    if (discountResponse.created) {
      // @ts-expect-error: strict-null-checks
      return discountResponse.discount.codes?.nodes[0]?.code;
    } else {
      const newDiscount = await getOrCreateDiscountCode(
        trackable.id,
        trackable.type,
      );

      return newDiscount
        ? // @ts-expect-error: strict-null-checks
          newDiscount.discount.codes?.nodes[0]?.code
        : undefined;
    }
  }
  return undefined;
}

function getUpsellRedirect(
  trackable: Trackable,
  settings: ReturnAppSettings,
  selectWindow: React.MutableRefObject<Window>,
  discountCode?: string,
  redirectUrl?: string,
): {
  logUpsellAndRedirect: (selectedVariant?: {
    id: string;
    handle: string;
  }) => Promise<void>;
} {
  const provider = trackable.provider;
  if (!provider) {
    throw new Error("Provider not found");
  }
  switch (provider) {
    case Provider.SHOPIFY:
      return {
        logUpsellAndRedirect: async (selectedVariant?: {
          id: string;
          handle: string;
        }) => {
          await logPageClick({
            trackableId: trackable.id,
            trackableType: trackable.type,
            eventType: "upsell",
            url: settings.storeUrl,
          });

          redirectToWebsiteWithDiscountCode({
            utmSource: "redo",
            utmMedium: "order_tracking",
            utmContent: "upsell",
            utmTerm: trackable.id,
            settings,
            window: selectWindow,
            discountCode,
            selectedProduct:
              redirectUrl && selectedVariant
                ? { url: redirectUrl, variantId: selectedVariant?.id }
                : undefined,
          });
        },
      };
    case Provider.BIGCOMMERCE:
      return {
        logUpsellAndRedirect: async (selectedVariant?: {
          id: string;
          handle: string;
        }) => {
          await logPageClick({
            trackableId: trackable.id,
            trackableType: trackable.type,
            eventType: "upsell",
            url: settings.storeUrl,
          });

          const url = selectedVariant
            ? new URL(`https://${settings.storeUrl}${selectedVariant.handle}`)
            : new URL(`https://${settings.storeUrl}`);

          const w = globalThis.open(url);
          if (w) {
            w.onunload = () => {
              if (w.closed) {
                // @ts-expect-error: strict-null-checks
                selectWindow.current = undefined;
              }
            };
            // save for use by advanced-exchange
            selectWindow.current = w;
          }
        },
      };
    case Provider.COMMENTSOLD:
      return {
        logUpsellAndRedirect: async () => {
          // TODO: Implement Commentsold upsell redirect
          throw new Error("Commentsold upsell redirect not implemented");
        },
      };
    case Provider.COMMERCE_CLOUD:
      return {
        logUpsellAndRedirect: async () => {
          // TODO: Implement Bigcommerce upsell redirect
          throw new Error("Commerce Cloud upsell redirect not implemented");
        },
      };
    case Provider.MANUAL:
      return {
        logUpsellAndRedirect: async () => {
          // TODO: Implement Bigcommerce upsell redirect
          throw new Error("Manual upsell redirect not implemented");
        },
      };
    case Provider.NETSUITE:
      return {
        logUpsellAndRedirect: async () => {
          // TODO: Implement Netsuite upsell redirect
          throw new Error("Netsuite OMS provider not implemented");
        },
      };
    case Provider.TIKTOK:
      return {
        logUpsellAndRedirect: async () => {
          // TODO: Implement TikTok upsell redirect
          throw new Error("TikTok OMS provider not implemented");
        },
      };
    case Provider.AMAZON:
      return {
        logUpsellAndRedirect: async () => {
          // TODO: Implement Amazon upsell redirect
          throw new Error("Amazon provider not implemented");
        },
      };
    default:
      assertNever(provider);
  }
}
